import { Controller } from "@hotwired/stimulus";
import { hide } from '../utils/stimulus_form_helper';
import "../../../vendor/assets/javascripts/jquery.payment.js";

// Connects to data-controller="credit-card"
export default class extends Controller {
  static targets = ['shadowCardNumberInput', 'cardNumberInput', 'cardLogo', 'creditCardForm'];

  connect() {
    this._shadowCardField();
  }

  maybeHighlightCardLogo() {
    const cardNumber = this.shadowCardNumberInputTarget.value;
    const cardType = this._getCreditCardType(cardNumber);

    if (cardType) {
      this._highlightMatchingLogo(cardType);
    } else {
      this._enableAllLogos();
    }

    // TODO: does this field even exist? I don't see it anywhere
    const ccTypeField = this.element.querySelector('#ccType');
    if (ccTypeField) {
      ccTypeField.value = cardType;
    }
  }

  syncCardNumber() {
    this.cardNumberInputTarget.value = this.shadowCardNumberInputTarget.value.replace(/ /g, '');
    const event = new Event('change', {bubbles: true});
    this.cardNumberInputTarget.dispatchEvent(event);
  }

  // Public validation entry point. Returns true when every field on the card
  // form passes; otherwise renders inline errors and returns false. Called from
  // the payment-forms submit guard and the NAB 3DS submit handler.
  validate() {
    if (!this.hasCreditCardFormTarget) {
      return true;
    }

    const form = this.creditCardFormTarget;
    let valid = true;
    let firstInvalid = null;

    this._validatableFields(form).forEach((field) => {
      const error = this._fieldError(field);
      if (error) {
        this._showError(field, error);
        valid = false;
        if (!firstInvalid) firstInvalid = field;
      } else {
        this._clearError(field);
      }
    });

    if (firstInvalid && typeof firstInvalid.focus === 'function') {
      firstInvalid.focus();
    }

    return valid;
  }

  // Clears the inline error for a single field as the user edits it, matching
  // the plugin's live unhighlight behaviour. Wired via data-action on inputs.
  clearFieldError(event) {
    this._clearError(event.target);
  }

  _validatableFields(form) {
    return Array.from(
      form.querySelectorAll('.required, .creditcard, .digits, .expiryDateNotInPast')
    ).filter((field) => this._isValidatable(field));
  }

  _isValidatable(field) {
    if (field.disabled) return false;
    // The real card-number input is hidden and mirrored from the visible shadow
    // input; validate the shadow input instead so the user sees the error.
    if (field === this.cardNumberInputTarget) return false;
    return true;
  }

  _fieldError(field) {
    const value = (field.value || '').trim();

    if (field.classList.contains('required') && value === '') {
      return 'This field is required.';
    }

    if (value === '') {
      // Non-required empty fields skip the remaining rules (mirrors optional()).
      return null;
    }

    if (field.classList.contains('digits') && !/^\d+$/.test(value)) {
      return 'Please enter only digits.';
    }

    if (field.classList.contains('creditcard') && !this._isValidLuhn(value.replace(/ /g, ''))) {
      return 'Please enter a valid credit card number.';
    }

    if (field.classList.contains('expiryDateNotInPast') && !this._isExpiryValid()) {
      return 'Please enter a valid expiry date.';
    }

    if (field.minLength > 0 && value.length < field.minLength) {
      return `Please enter at least ${field.minLength} characters.`;
    }

    if (field.maxLength > 0 && value.length > field.maxLength) {
      return `Please enter no more than ${field.maxLength} characters.`;
    }

    return null;
  }

  _isExpiryValid() {
    const form = this.creditCardFormTarget;
    const month = form.querySelector('#expiry_month')?.value;
    const year = form.querySelector('#expiry_year')?.value;

    if (!month || !year) {
      return false;
    }

    const today = new Date();
    // Date's month arg is 0-indexed, so passing the 1-indexed form month lands
    // on the 1st of the month AFTER expiry — i.e. the card stays valid through
    // the last day of its expiry month.
    const expiryEnd = new Date(year, month, 1);

    // Also check the month explicitly: the Date constructor rolls months > 12
    // into the following year, which would otherwise mask an invalid month.
    return expiryEnd.getTime() > today.getTime() && Number(month) <= 12;
  }

  _isValidLuhn(value) {
    if (!/^\d+$/.test(value)) {
      return false;
    }

    let sum = 0;
    let shouldDouble = false;
    for (let i = value.length - 1; i >= 0; i--) {
      let digit = parseInt(value.charAt(i), 10);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  }

  // Expiry month and year share a single error message positioned after the
  // year field, matching the plugin's `groups` config.
  _errorAnchor(field) {
    if (field.id === 'expiry_month' || field.id === 'expiry_year') {
      return this.creditCardFormTarget.querySelector('#expiry_year') || field;
    }
    return field;
  }

  _showError(field, message) {
    const anchor = this._errorAnchor(field);
    const controlGroup = anchor.closest('div.control-group');
    if (controlGroup) {
      controlGroup.classList.add('error');
      controlGroup.classList.remove('valid');
    }

    const controls = anchor.closest('.controls') || anchor.parentElement;
    if (!controls) return;

    let error = controls.querySelector('span.help-inline.validation-error');
    if (!error) {
      error = document.createElement('span');
      error.className = 'help-inline validation-error';
      controls.appendChild(error);
    }
    error.textContent = message;
  }

  _clearError(field) {
    const anchor = this._errorAnchor(field);
    const controlGroup = anchor.closest('div.control-group');
    if (controlGroup) {
      controlGroup.classList.remove('error');
      controlGroup.classList.add('valid');
    }

    const controls = anchor.closest('.controls') || anchor.parentElement;
    const error = controls?.querySelector('span.help-inline.validation-error');
    if (error) error.remove();
  }

  _getCreditCardType(accountNumber) {
    if (/^5[1-5]/.test(accountNumber) || /^2[2-7]/.test(accountNumber)) {
      return 'mastercard';
    }
    if (/^4/.test(accountNumber)) {
      return 'visa';
    }
    return undefined;
  }

  _shadowCardField() {
    if (this.hasCardNumberInputTarget) {
      this.cardNumberInputTarget.id = 'card_number_original';
      hide(this.cardNumberInputTarget);
      this._createShadowCardInput();

      $(this.shadowCardNumberInputTarget).payment('formatCardNumber');
    }
  }

  _highlightMatchingLogo(cardType) {
    this.cardLogoTargets.forEach((logo) => {
      logo.classList.toggle('disabled', !logo.classList.contains(cardType));
    });
  }

  _enableAllLogos() {
    this.cardLogoTargets.forEach((logo) => {
      logo.classList.remove('disabled');
    });
  }

  _createShadowCardInput() {
    const shadowInput = document.createElement('input');
    shadowInput.type = 'text';
    shadowInput.id = 'card_number';
    shadowInput.className = 'required creditcard no-mouseflow sensitive';
    shadowInput.setAttribute('data-credit-card-target', 'shadowCardNumberInput');
    shadowInput.setAttribute('data-action', 'change->credit-card#maybeHighlightCardLogo keyup->credit-card#maybeHighlightCardLogo change->credit-card#syncCardNumber keyup->credit-card#syncCardNumber input->credit-card#clearFieldError');

    this.cardNumberInputTarget.insertAdjacentElement('afterend', shadowInput);
  }
}
